import { z } from 'zod';

export const NOTIFICATION_KINDS = [
  'spark_received',
  'whisper_reply',
  'circle_mention',
  'mod_notice',
  'appeal',
  'broadcast',
  'system',
] as const;
export const NotificationKindSchema = z.enum(NOTIFICATION_KINDS);
export type NotificationKind = z.infer<typeof NotificationKindSchema>;

/** Groups the raw kinds into the handful of tabs the Alerts screen shows —
 * enough to triage at a glance without listing all seven kinds. */
export const NOTIFICATION_CATEGORIES = {
  sparks: ['spark_received'],
  whispers: ['whisper_reply'],
  circles: ['circle_mention'],
  notices: ['mod_notice', 'appeal', 'broadcast', 'system'],
} as const satisfies Record<string, readonly NotificationKind[]>;
export type NotificationCategory = keyof typeof NOTIFICATION_CATEGORIES;
export const NOTIFICATION_CATEGORY_KEYS = Object.keys(
  NOTIFICATION_CATEGORIES,
) as NotificationCategory[];
export const NotificationCategorySchema = z.enum(['all', ...NOTIFICATION_CATEGORY_KEYS] as [
  string,
  ...string[],
]);

export const NotificationSchema = z.object({
  id: z.string(),
  kind: NotificationKindSchema,
  title: z.string().max(120),
  body: z.string().max(240),
  /** Client route to open, e.g. "/app/whisper/abc". */
  href: z.string().nullable(),
  read: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type Notification = z.infer<typeof NotificationSchema>;

/* GET /api/notifications?category=&cursor=&limit= */
export const NotificationsResponseSchema = z.object({
  notifications: z.array(NotificationSchema),
  /** Total unread across every category, not just this page/filter. */
  unread: z.number().int().nonnegative(),
  /** Unread count per category, for the tab badges. */
  unreadByCategory: z.record(z.string(), z.number().int().nonnegative()),
  nextCursor: z.string().nullable(),
});
export type NotificationsResponse = z.infer<typeof NotificationsResponseSchema>;

/* POST /api/notifications/read */
export const MarkReadSchema = z.object({
  ids: z.array(z.string()).max(100).optional(),
  all: z.boolean().optional(),
});
export type MarkRead = z.infer<typeof MarkReadSchema>;
